import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { StockItemCard } from '../../components/nursery';
import { FolderTree, Package, Plus, Tags } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useStockSeat } from '../../hooks/useStockSeat';
import type { NurseryStockItem, NurseryCustomerTier } from '../../types/nursery';
import { groupByCrop, NO_CROP } from '../../utils/cropGroups';
import { RetailCategoriesDialog } from '../../components/nursery/RetailCategoriesDialog';

// Plants by how they are grown; merchandise (potting mix, pots…) on its own.
const STOCK_TYPE_TABS: { label: 'allPlants' | 'bareroot' | 'container' | 'merchandise'; value: string }[] = [
  { label: 'allPlants',   value: 'all' },
  { label: 'bareroot',    value: 'B' },
  { label: 'container',   value: 'P' },
  { label: 'merchandise', value: 'merchandise' },
];

const NO_CATEGORY = 'No category';

const text = (v?: string | null) => v ?? '';

/** Within a crop: cultivar, then rootstock, then tree type. */
const byCultivar = (a: NurseryStockItem, b: NurseryStockItem) =>
  text(a.cultivar_name).localeCompare(text(b.cultivar_name))
  || text(a.rootstock_name).localeCompare(text(b.rootstock_name))
  || text(a.tree_type_code).localeCompare(text(b.tree_type_code));

function StockItemList() {
  const { t } = useTranslation('nursery', { keyPrefix: 'StockItemList' });
  const navigate = useNavigate();
  // Creating a new SKU requires nursery.admin; read access stays available
  // to any licensed user. We still render the list for everyone, but hide
  // the "New" CTAs when the user lacks the admin seat.
  const { hasAccess: canAdmin } = useStockSeat('admin');
  const [items, setItems] = useState<NurseryStockItem[]>([]);
  const [tiers, setTiers] = useState<NurseryCustomerTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stockType, setStockType] = useState('all');
  const [crop, setCrop] = useState('all');
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const merch = stockType === 'merchandise';

  useEffect(() => {
    nurseryService.getCustomerTiers().then((r) => { if (r.success) setTiers(r.data || []); });
  }, []);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryService.getStockItems(
        merch
          ? { active: true, kind: 'merchandise' }
          : { active: true, kind: 'plant', ...(stockType !== 'all' ? { stock_type: stockType } : {}) },
      );
      if (res.success) {
        setItems(res.data || []);
      }
    } catch (e: any) {
      setError(e?.message || t('loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stockType]);

  // Plants group by crop; merchandise by the shop's category.
  const groups = useMemo(() => (merch
    ? groupByCrop(items, (i) => i.category_name || NO_CATEGORY,
      (a, b) => text(a.name).localeCompare(text(b.name)))
    : groupByCrop(items, (i) => i.crop_type_name, byCultivar)), [items, merch]);
  // A crop chosen under one stock type may have nothing under the other.
  const cropShown = groups.some(g => g.crop === crop) ? crop : 'all';
  const shown = cropShown === 'all' ? groups : groups.filter(g => g.crop === cropShown);
  // A server from before the crop was sent names none: no headings, rather than one saying "No crop type".
  const hasCrops = groups.some(g => g.crop !== NO_CROP);
  const groupName = (c: string) => (c === NO_CROP ? t('noCrop') : c === NO_CATEGORY ? t('noCategory') : c);

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Package className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap items-center gap-2">
            {/* Products sell several of these under one name, so they are set up from here. */}
            <Button variant="outline" size="sm" onClick={() => navigate('/nursery/products')}>
              <Tags className="h-4 w-4" />
              {t('products')}
            </Button>
            {canAdmin && (
              <Button variant="outline" size="sm" onClick={() => setCategoriesOpen(true)}>
                <FolderTree className="h-4 w-4" />
                {t('shopCategories')}
              </Button>
            )}
            {canAdmin && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate(merch ? '/nursery/stock-items/new?kind=merchandise' : '/nursery/stock-items/new')}
              >
                <Plus className="h-4 w-4" />
                {t('newStockItem')}
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stock type filter */}
        <div className="flex gap-x-3 gap-y-2 flex-wrap items-center justify-between mb-6">
          <div className="flex gap-1 flex-wrap">
            {STOCK_TYPE_TABS.map(tab => (
              <button
                key={tab.value}
                onClick={() => setStockType(tab.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                stockType === tab.value
                  ? 'bg-primary-600 text-white font-medium'
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
              >
                {t(`tabs.${tab.label}`)}
              </button>
            ))}
          </div>
          {hasCrops && groups.length > 1 && (
            <select
              aria-label={merch ? t('category') : t('cropType')}
              value={cropShown}
              onChange={(e) => setCrop(e.target.value)}
              className="w-full sm:w-auto border border-neutral-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">{merch ? t('allCategories') : t('allCrops')}</option>
              {groups.map(g => (
                <option key={g.crop} value={g.crop}>{t('groupOption', { group: groupName(g.crop), n: g.items.length })}</option>
              ))}
            </select>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-36 bg-neutral-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-neutral-400">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-neutral-600">{merch ? t('emptyMerch') : t('emptyItems')}</p>
            <p className="text-sm mt-1">
              {!canAdmin
                ? t('emptySeat')
                : merch
                  ? t('emptyMerchAdmin')
                  : t('emptyAdmin')}
            </p>
            {canAdmin && (
              <Button
                variant="primary"
                className="mt-4"
                onClick={() => navigate(merch ? '/nursery/stock-items/new?kind=merchandise' : '/nursery/stock-items/new')}
              >
                <Plus className="h-4 w-4" /> {t('newStockItem')}
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {shown.map(group => (
              <section key={group.crop}>
                {hasCrops && (
                  <h2 className="flex items-baseline gap-2 mb-3 text-base font-semibold text-neutral-800">
                    <span className="min-w-0 break-words">{groupName(group.crop)}</span>
                    <span className="text-xs font-normal text-neutral-500">{group.items.length}</span>
                  </h2>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.items.map(item => (
                    <StockItemCard
                      key={item.id}
                      stockItem={item}
                      editable={canAdmin}
                      tiers={tiers}
                      onUpdated={(updated) => setItems(prev => prev.map(i => (i.id === updated.id ? { ...i, ...updated } : i)))}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
      {categoriesOpen && (
        <RetailCategoriesDialog onClose={() => setCategoriesOpen(false)} onChanged={() => void loadItems()} />
      )}
    </>
  );
}

export default StockItemList;
